// Wave 20.49cs — Wind "still": the app's particle sim, frozen.
//
// Synchronous port of components/WindParticles.tsx (same PNG field
// decode, same sampling, same non-linear advection, same speed
// palette, same fade). Instead of animating on a live map it runs N
// ticks against a MapFrame projection on an offscreen canvas and
// returns that canvas — the accumulated comet-tails ARE the
// visualization, in exactly the visual language the rider knows from
// the Wake / recording / route-planner maps. Composited over the
// share card's static map.

import type { MapFrame } from './mapFrame';

const WIND_MAX = 51; // m/s — server PNG encoding contract
const SPEED_EXPONENT = 1.35;
const SPEED_BASE = 0.11;
const FADE_ALPHA = 0.045;
const MAX_AGE = 90;

export interface WindField {
  width: number;
  height: number;
  bbox: [number, number, number, number];
  data: Uint8ClampedArray;
}

/** Fetch + decode the wind PNG for `bbox` at ISO time `t` through
 *  the api proxy. Returns null on any failure — the card just ships
 *  without wind rather than failing. */
export async function fetchWindField(
  apiBase: string,
  jwt: string | null,
  bbox: [number, number, number, number],
  tIso: string,
  size = '768x576',
): Promise<WindField | null> {
  try {
    const [w, s, e, n] = bbox;
    const url =
      `${apiBase}/weather/wind/png?bbox=${encodeURIComponent(`${w.toFixed(3)},${s.toFixed(3)},${e.toFixed(3)},${n.toFixed(3)}`)}` +
      `&t=${encodeURIComponent(tIso)}&size=${size}`;
    const res = await fetch(url, { headers: jwt ? { Authorization: `Bearer ${jwt}` } : undefined });
    if (!res.ok) return null;
    const bmp = await createImageBitmap(await res.blob());
    const c = document.createElement('canvas');
    c.width = bmp.width;
    c.height = bmp.height;
    const ctx = c.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bmp, 0, 0);
    const img = ctx.getImageData(0, 0, bmp.width, bmp.height);
    return { width: bmp.width, height: bmp.height, bbox, data: img.data };
  } catch {
    return null;
  }
}

function sample(field: WindField, lng: number, lat: number): [number, number] | null {
  const [w, s, e, n] = field.bbox;
  if (lat > n || lat < s) return null;
  const east = e < w ? e + 360 : e;
  let lngWrap = lng;
  if (e < w && lngWrap < w) lngWrap += 360;
  if (lngWrap < w || lngWrap > east) return null;
  const x = Math.floor(((lngWrap - w) / (east - w)) * field.width);
  const y = Math.floor(((n - lat) / (n - s)) * field.height);
  if (x < 0 || x >= field.width || y < 0 || y >= field.height) return null;
  const i = (y * field.width + x) * 4;
  if (field.data[i + 3] < 128) return null;
  return [((field.data[i] - 128) / 128) * WIND_MAX, ((field.data[i + 1] - 128) / 128) * WIND_MAX];
}

function speedColor(speed: number): string {
  const t = Math.min(1, speed / (WIND_MAX * 0.6));
  if (t < 0.33) {
    const k = t / 0.33;
    return `rgba(80,160,255,${0.55 + 0.2 * k})`;
  } else if (t < 0.66) {
    const k = (t - 0.33) / 0.33;
    return `rgba(${Math.round(80 + 175 * k)},${Math.round(160 + 60 * k)},${Math.round(255 - 60 * k)},0.85)`;
  }
  const k = (t - 0.66) / 0.34;
  return `rgba(255,${Math.round(220 - 130 * k)},${Math.round(120 - 100 * k)},0.9)`;
}

/** Run the sim for `steps` ticks over `frame` and return the
 *  transparent trail layer, sized frame.width × frame.height (CSS px)
 *  at `scale` device pixels. */
export function renderWindStill(
  field: WindField,
  frame: MapFrame,
  steps = 150,
  scale = 2,
  seed = 7,
): HTMLCanvasElement {
  const w = frame.width;
  const h = frame.height;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(w * scale);
  canvas.height = Math.round(h * scale);
  const ctx = canvas.getContext('2d')!;
  ctx.setTransform(scale, 0, 0, scale, 0, 0);

  // Deterministic PRNG so the same ride renders the same card.
  let s = seed >>> 0;
  const rnd = () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };

  const count = Math.round((w * h) / 450); // ~1500 at 948×711
  const parts = Array.from({ length: count }, () => ({
    x: rnd() * w,
    y: rnd() * h,
    age: rnd() * MAX_AGE,
  }));
  const spanDeg = Math.max(frame.bbox[2] - frame.bbox[0], frame.bbox[3] - frame.bbox[1]);
  const lineW = spanDeg > 30 ? 1.8 : spanDeg > 5 ? 1.4 : 1.1;

  for (let step = 0; step < steps; step++) {
    ctx.globalCompositeOperation = 'destination-in';
    ctx.fillStyle = `rgba(0,0,0,${1 - FADE_ALPHA})`;
    ctx.fillRect(0, 0, w, h);
    ctx.globalCompositeOperation = 'source-over';
    ctx.lineWidth = lineW;
    for (const p of parts) {
      p.age++;
      if (p.age > MAX_AGE) {
        p.x = rnd() * w;
        p.y = rnd() * h;
        p.age = 0;
        continue;
      }
      const { lng, lat } = frame.unproject(p.x, p.y);
      const uv = sample(field, lng, lat);
      if (!uv) { p.age = MAX_AGE + 1; continue; }
      const [u, v] = uv;
      const speed = Math.hypot(u, v);
      const k = speed > 0 ? Math.pow(speed, SPEED_EXPONENT - 1) * SPEED_BASE : 0;
      const nx = p.x + u * k;
      const ny = p.y - v * k;
      ctx.strokeStyle = speedColor(speed);
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
      ctx.lineTo(nx, ny);
      ctx.stroke();
      p.x = nx;
      p.y = ny;
      if (p.x < 0 || p.x > w || p.y < 0 || p.y > h) p.age = MAX_AGE + 1;
    }
  }
  return canvas;
}
